import { useState } from 'react';
import { getJournalEntries, deleteJournalEntry as deleteJournalEntryApi, postJournalEntry, unpostJournalEntry } from '../services/api';
import { createEntityStore } from '@redpanda/utils';
import { toast } from 'react-hot-toast';
import i18n from '../i18n';

interface JournalEntryDetail {
  id: string;
  accountId: string;
  debitAmount: number;
  creditAmount: number;
  description?: string;
  account: {
    id: string;
    code: string;
    name: string;
    type: string;
  };
}

interface JournalEntry {
  id: string;
  userId: string; // 建立者（刪除權限判斷用）
  date: string;
  description: string;
  reference?: string;
  totalAmount: number;
  isPosted: boolean;
  details: JournalEntryDetail[];
  createdAt: string;
  updatedAt: string;
}

const useJournalStoreBase = createEntityStore<JournalEntry>({
  entityName: 'journal',
  fetchFn: (page: number) => getJournalEntries(page, 20),
  deleteFn: deleteJournalEntryApi,
  toast,
  messages: {
    deleteSuccess: () => i18n.t('common.messages.deleteSuccess'),
    deleteError: () => i18n.t('common.messages.deleteFailed'),
    fetchError: () => i18n.t('common.messages.fetchFailed'),
  },
});

export const useJournalStore = () => {
  const store = useJournalStoreBase();

  // 個別過帳狀態管理
  const [postingStates, setPostingStates] = useState<{ [id: string]: boolean }>({});
  
  // 自定義刪除邏輯，刪除後刷新會計科目使用計數
  const confirmDeleteWithAccountRefresh = async () => {
    // 刪除前先取得分錄年份
    const journalToDelete = store.items.find(j => j.id === store.itemToDeleteId);
    const journalYear = journalToDelete ? new Date(journalToDelete.date).getFullYear().toString() : undefined;

    await store.confirmDelete();

    // 立即通知會計科目store刷新數據
    import('./accountStore').then(({ useAccountStoreBase }) => {
      const accountStore = useAccountStoreBase.getState();
      accountStore.refreshData();
    });

    // 清除被刪除分錄年份的報表快取
    if (journalYear) {
      import('./reportStore').then(({ useReportStore }) => {
        const reportStore = useReportStore.getState();
        reportStore.clearYearCache(journalYear);
      });
    }
  };

  // 過帳分錄
  const postJournal = async (id: string) => {
    // 設置loading狀態
    setPostingStates(prev => ({ ...prev, [id]: true }));

    try {
      const response = await postJournalEntry(id);
      // 更新本地狀態
      store.updateItem(id, { isPosted: true });
      toast.success(i18n.t('common.messages.postSuccess'));

      // 刷新會計科目數據（因為餘額可能已變更）
      import('./accountStore').then(({ useAccountStoreBase }) => {
        const accountStore = useAccountStoreBase.getState();
        accountStore.refreshData();
      });

      // 清除當前年份的報表快取（從分錄日期取得年份）
      import('./reportStore').then(({ useReportStore }) => {
        const reportStore = useReportStore.getState();
        const journalYear = new Date(store.items.find(j => j.id === id)?.date || new Date()).getFullYear().toString();
        reportStore.clearYearCache(journalYear);
      });

      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || i18n.t('common.messages.postFailed');
      toast.error(errorMessage);
      throw error;
    } finally {
      // 清除loading狀態
      setPostingStates(prev => ({ ...prev, [id]: false }));
    }
  };

  // 取消過帳分錄
  const unpostJournal = async (id: string) => {
    // 設置loading狀態
    setPostingStates(prev => ({ ...prev, [id]: true }));

    try {
      const response = await unpostJournalEntry(id);
      // 更新本地狀態
      store.updateItem(id, { isPosted: false });
      toast.success(i18n.t('common.messages.unpostSuccess'));

      // 刷新會計科目數據（因為餘額可能已變更）
      import('./accountStore').then(({ useAccountStoreBase }) => {
        const accountStore = useAccountStoreBase.getState();
        accountStore.refreshData();
      });

      // 清除當前年份的報表快取（從分錄日期取得年份）
      import('./reportStore').then(({ useReportStore }) => {
        const reportStore = useReportStore.getState();
        const journalYear = new Date(store.items.find(j => j.id === id)?.date || new Date()).getFullYear().toString();
        reportStore.clearYearCache(journalYear);
      });

      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.error || error.message || i18n.t('common.messages.unpostFailed');
      toast.error(errorMessage);
      throw error;
    } finally {
      // 清除loading狀態
      setPostingStates(prev => ({ ...prev, [id]: false }));
    }
  };

  return {
    journals: store.items,
    pagination: store.pagination,
    currentPage: store.currentPage,
    error: store.error,
    isLoading: store.isLoading,
    isPageLoading: store.isPageLoading,
    showDeleteConfirmModal: store.showDeleteConfirmModal,
    itemToDeleteId: store.itemToDeleteId,
    isDeleting: store.isDeleting,
    fetchJournals: store.fetchItems,
    setCurrentPage: store.setCurrentPage,
    handleDelete: store.handleDelete,
    confirmDelete: confirmDeleteWithAccountRefresh,
    closeDeleteModal: store.closeDeleteModal,
    lastOptimisticUpdate: store.lastOptimisticUpdate,
    refreshData: store.refreshData,
    updateJournal: store.updateItem,
    addJournal: store.addItem,
    postJournal,
    unpostJournal,
    postingStates,
  };
};

// 導出基礎 store 供其他模組使用（如批量操作）
export { useJournalStoreBase };